/**
 * Estimated amount owed — the security-interest equivalent of the Assets
 * panel's valuations list.
 *
 * Purpose: a security interest can have several competing views of what it
 * secures (a creditor's Proof of Debt, a balance in a loan statement, the
 * practitioner's own figure, and the running total of the debts it is linked
 * to). This section lists them all as cards, lets the practitioner endorse one
 * as the figure of record, and lets them add their own.
 *
 * The endorsed figure is mirrored onto `securityInterestEntries.owedAmount` by
 * the backend, so everything downstream (secured netting, the outcome
 * waterfall, exports) keeps reading a single number.
 *
 * @component
 * @example
 * <OwedEstimatesSection entry={entry} isViewer={isViewer} />
 */

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Check, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { cn } from "@/utils/misc";
import {
  useJobLinks,
  useJobRecordIndex,
  linksForRecord,
  counterpartOf,
  RECORD_TYPE_ICON,
  type RecordType,
} from "../shared-panels/linkShared";
import { compareOwedEstimatesForDisplay } from "../shared-panels/owedEstimateOrder";
import type {
  SecurityInterestEntryUI,
  SecurityInterestOwedEstimateUI,
} from "./types";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const SOURCE_LABEL: Record<string, string> = {
  proof_of_debt: "Proof of debt",
  document: "Document",
  manual: "Practitioner estimate",
};

/** Single-character provenance badge, mirroring the asset valuation cards. */
const SOURCE_INITIAL: Record<string, string> = {
  proof_of_debt: "P",
  document: "D",
  manual: "M",
};

const SOURCE_BADGE_CLASS: Record<string, string> = {
  proof_of_debt:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  document: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
  manual:
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400",
};

/** Parses a typed figure, tolerating pasted values like "$1,200,000". */
function parseAmount(raw: string): number | null {
  const cleaned = raw.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// Linked-debt rollup (the calculated estimate)
// ---------------------------------------------------------------------------

interface LinkedDebt {
  id: string;
  type: RecordType;
  name: string;
  subtitle?: string;
  amount?: number;
}

/**
 * The debts this security interest is linked to, and their total. Mirrors the
 * backend rollup so the calculated card shows the same figure the derivation
 * would pick.
 */
function useLinkedDebts(
  jobId: Id<"jobs">,
  securityInterestId: string,
): { total: number; debts: LinkedDebt[] } {
  const links = useJobLinks(jobId);
  const records = useJobRecordIndex(jobId);
  return useMemo(() => {
    const mine = linksForRecord(links, "security_interest", securityInterestId);
    const debts: LinkedDebt[] = [];
    for (const link of mine) {
      const cp = counterpartOf(link, securityInterestId);
      // Linked creditors AND linked loans are both debts the SI secures, so
      // both feed the rollup (mirrors the backend sync).
      if (cp.type !== "creditor" && cp.type !== "loan") continue;
      const ref = records.byId.get(cp.id);
      debts.push({
        id: cp.id,
        type: cp.type,
        name: ref?.title ?? (cp.type === "loan" ? "Loan" : "Creditor"),
        subtitle: ref?.subtitle,
        amount: ref?.amount,
      });
    }
    const total = debts.reduce((sum, d) => sum + (d.amount ?? 0), 0);
    return { total, debts };
  }, [links, records, securityInterestId]);
}

/** Collapsible "show the working" for the linked-debt rollup. */
function LinkedDebtBreakdown({
  debts,
  total,
}: {
  debts: LinkedDebt[];
  total: number;
}) {
  const [open, setOpen] = useState(false);
  if (debts.length === 0) return null;

  return (
    <div className="pt-1.5">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            open && "rotate-90",
          )}
        />
        {open ? "Hide calculation" : "Show calculation"}
      </button>
      {open && (
        <div className="mt-2 divide-y divide-border overflow-hidden rounded-md border border-border">
          {debts.map((d) => {
            const RowIcon = RECORD_TYPE_ICON[d.type];
            return (
              <div
                key={d.id}
                className="flex items-start justify-between gap-3 px-3 py-2"
              >
                <div className="flex min-w-0 items-start gap-2">
                  <RowIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">{d.name}</p>
                    {d.subtitle && (
                      <p className="truncate text-xs text-muted-foreground">
                        {d.subtitle}
                      </p>
                    )}
                  </div>
                </div>
                <span className="shrink-0 text-sm tabular-nums text-foreground">
                  {d.amount != null ? AUD.format(d.amount) : "\u2014"}
                </span>
              </div>
            );
          })}
          <div className="flex items-center justify-between gap-3 bg-muted/40 px-3 py-2">
            <span className="text-sm font-medium text-foreground">
              Total{" \u00B7 "}
              {debts.length} linked debt{debts.length > 1 ? "s" : ""}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
              {AUD.format(total)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estimate card
// ---------------------------------------------------------------------------

function EstimateCard({
  estimate,
  isSelected,
  canEdit,
  onSelect,
  onUpdateAmount,
  onDelete,
}: {
  estimate: SecurityInterestOwedEstimateUI;
  isSelected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdateAmount: (amount: number) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(estimate.amount));

  const label =
    estimate.label ?? SOURCE_LABEL[estimate.source] ?? estimate.source;
  const asAt = fmtDate(estimate.asAtDate);

  function commit() {
    const parsed = parseAmount(draft);
    if (parsed != null && parsed !== estimate.amount) onUpdateAmount(parsed);
    setEditing(false);
  }

  return (
    <div
      onClick={canEdit && !editing ? onSelect : undefined}
      className={cn(
        "group rounded-lg border p-3 transition-colors",
        isSelected
          ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20"
          : "border-border hover:border-muted-foreground/30",
        canEdit && "cursor-pointer",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold",
              SOURCE_BADGE_CLASS[estimate.source] ??
                "bg-muted text-muted-foreground",
            )}
          >
            {SOURCE_INITIAL[estimate.source] ?? "?"}
          </div>
          <span className="truncate text-xs font-semibold text-foreground">
            {label}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {asAt && (
            <span className="whitespace-nowrap text-[10px] text-muted-foreground">
              {asAt}
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
              title="Remove this estimate"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
          {isSelected && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
              <Check className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-2">
        {editing && canEdit ? (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setDraft(String(estimate.amount));
                  setEditing(false);
                }
              }}
              className="h-7 w-36 text-sm font-semibold tabular-nums"
              autoFocus
            />
            <Button size="sm" className="h-7 text-xs" onClick={commit}>
              Save
            </Button>
          </div>
        ) : (
          <span
            className={cn(
              "text-sm font-bold tabular-nums",
              isSelected
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-foreground",
              canEdit && "cursor-text",
            )}
            onClick={(e) => {
              if (!canEdit) return;
              e.stopPropagation();
              setDraft(String(estimate.amount));
              setEditing(true);
            }}
          >
            {AUD.format(estimate.amount)}
            {canEdit && (
              <Pencil className="ml-1 inline-block h-2.5 w-2.5 opacity-30" />
            )}
          </span>
        )}
      </div>

      {estimate.notes && (
        <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">
          {estimate.notes}
        </p>
      )}
      {estimate.sourceDocument && (
        <p className="mt-2 truncate text-[10px] text-muted-foreground">
          {estimate.sourceDocument.fileName}
          {estimate.sourceDocument.section &&
            ` \u00B7 ${estimate.sourceDocument.section}`}
        </p>
      )}
    </div>
  );
}

/**
 * The linked-debt rollup rendered as a selectable card so it sits alongside the
 * document-sourced estimates. Selecting it clears the practitioner's
 * endorsement, which is what makes the figure track the links again.
 */
function CalculatedCard({
  total,
  debts,
  isSelected,
  canEdit,
  onSelect,
}: {
  total: number;
  debts: LinkedDebt[];
  isSelected: boolean;
  canEdit: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={canEdit ? onSelect : undefined}
      className={cn(
        "rounded-lg border p-3 transition-colors",
        isSelected
          ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20"
          : "border-border hover:border-muted-foreground/30",
        canEdit && "cursor-pointer",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-bold text-muted-foreground">
            {"\u03A3"}
          </div>
          <span className="truncate text-xs font-semibold text-foreground">
            Calculated from {debts.length} linked debt
            {debts.length > 1 ? "s" : ""}
          </span>
        </div>
        {isSelected && (
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500">
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
      </div>
      <div className="mt-2">
        <span
          className={cn(
            "text-sm font-bold tabular-nums",
            isSelected
              ? "text-emerald-700 dark:text-emerald-400"
              : "text-foreground",
          )}
        >
          {AUD.format(total)}
        </span>
      </div>
      <LinkedDebtBreakdown debts={debts} total={total} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add form
// ---------------------------------------------------------------------------

function AddEstimateForm({
  onAdd,
  onCancel,
}: {
  onAdd: (input: { amount: number; asAtDate?: string; notes?: string }) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [asAt, setAsAt] = useState("");
  const [notes, setNotes] = useState("");

  const parsed = parseAmount(amount);

  return (
    <div className="space-y-3 rounded-lg border-2 border-dashed border-violet-200 p-4 dark:border-violet-800">
      <p className="text-sm font-semibold text-violet-700 dark:text-violet-400">
        Add practitioner estimate
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Amount ($)
        </label>
        <Input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          className="h-9 text-sm tabular-nums"
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          As at
        </label>
        <DatePicker value={asAt} onChange={setAsAt} className="w-full" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notes
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Basis for this estimate..."
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={parsed == null}
          onClick={() => {
            if (parsed == null) return;
            onAdd({
              amount: parsed,
              asAtDate: asAt.trim() || undefined,
              notes: notes.trim() || undefined,
            });
          }}
        >
          Add &amp; select
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

/**
 * One-line explanation of where the headline figure comes from, so the
 * practitioner knows what is being netted against the linked assets.
 */
function basisHint(args: {
  hasSelection: boolean;
  selectedLabel?: string;
  linkedCount: number;
  estimateCount: number;
  amount?: number;
}): { text: string; isWarning: boolean } | null {
  if (args.amount == null) {
    return {
      text: "No amount owed yet. Add an estimate, or link a creditor or loan.",
      isWarning: true,
    };
  }
  if (args.hasSelection) {
    return {
      text: `Using the ${(args.selectedLabel ?? "selected estimate").toLowerCase()}.`,
      isWarning: false,
    };
  }
  if (args.linkedCount > 0) {
    return {
      text: `Auto-calculated from ${args.linkedCount} linked debt${
        args.linkedCount > 1 ? "s" : ""
      }. Select an estimate below to use a different figure.`,
      isWarning: false,
    };
  }
  return {
    text:
      args.estimateCount > 1
        ? "Using the most authoritative estimate. Select one below to pin it."
        : "Taken from the only estimate on file.",
    isWarning: false,
  };
}

/**
 * Mirrors the backend seed so rows written before estimates existed still show
 * their figure as a card. The synthesised id matches the one the mutations seed
 * with, so editing or selecting it resolves to the same estimate server-side.
 */
function estimatesWithLegacySeed(
  entry: SecurityInterestEntryUI,
): SecurityInterestOwedEstimateUI[] {
  const existing = entry.owedEstimates ?? [];
  if (existing.length > 0) return existing;
  // No source means the figure came from the linked-debt rollup, which already
  // has its own card.
  if (entry.owedAmount == null || entry.owedAmountSource == null) return [];
  return [
    {
      id: "legacy",
      source: entry.owedAmountSource,
      label: SOURCE_LABEL[entry.owedAmountSource],
      amount: entry.owedAmount,
      asAtDate: entry.owedAmountAsAt,
      recordedAt: 0,
      recordedBy:
        entry.owedAmountSource === "manual" ? "Practitioner" : "Morgan",
    },
  ];
}

export function OwedEstimatesSection({
  entry,
  isViewer,
}: {
  entry: SecurityInterestEntryUI;
  isViewer?: boolean;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const canEdit = !isViewer;

  const { total: linkedTotal, debts } = useLinkedDebts(entry.jobId, entry._id);
  const estimates = estimatesWithLegacySeed(entry);
  // Show the creditor's own figures (Proof of Debt) above document balances and
  // the company's records. The auto-calculated linked-debt card stays first.
  const orderedEstimates = [...estimates].sort(compareOwedEstimatesForDisplay);
  // Mirrors the backend's endorse-on-seed: a pre-estimates manual figure is
  // pinned ahead of the linked-debt rollup until the practitioner says
  // otherwise, so the synthesised card shows as endorsed before any write
  // has persisted the seed.
  const legacyManualPinned =
    (entry.owedEstimates ?? []).length === 0 &&
    entry.selectedOwedEstimate == null &&
    entry.owedAmount != null &&
    entry.owedAmountSource === "manual";
  const selectedId =
    entry.selectedOwedEstimate?.estimateId ??
    (legacyManualPinned ? "legacy" : undefined);
  const selected = estimates.find((e) => e.id === selectedId);
  // A selection pointing at a deleted estimate would otherwise leave both the
  // calculated card and every estimate card looking unselected.
  const hasSelection = selected != null;

  const addEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .addSecurityInterestOwedEstimate,
    ),
  });
  const updateEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .updateSecurityInterestOwedEstimate,
    ),
  });
  const deleteEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .deleteSecurityInterestOwedEstimate,
    ),
  });
  const selectEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .selectSecurityInterestOwedEstimate,
    ),
  });
  const useCalculated = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.securityInterests.publicMutations
        .resetSecurityInterestOwedToCalculated,
    ),
  });

  const entryId = entry._id as Id<"securityInterestEntries">;
  const hint = basisHint({
    hasSelection,
    selectedLabel: selected
      ? (selected.label ?? SOURCE_LABEL[selected.source])
      : undefined,
    linkedCount: debts.length,
    estimateCount: estimates.length,
    amount: entry.owedAmount,
  });
  const headlineAsAt = fmtDate(entry.owedAmountAsAt);

  return (
    <>
      {/* Headline figure — the number every other view of this job reads. */}
      <div className="py-2">
        <span className="mb-0.5 block text-sm text-muted-foreground">
          Amount owed
        </span>
        <span className="text-2xl font-bold tabular-nums text-foreground">
          {entry.owedAmount != null ? AUD.format(entry.owedAmount) : "\u2014"}
        </span>
        {headlineAsAt && (
          <span className="ml-2 text-xs text-muted-foreground">
            as at {headlineAsAt}
          </span>
        )}
        {hint && (
          <p
            className={cn(
              "mt-1 text-xs",
              hint.isWarning
                ? "text-rose-600 dark:text-rose-400"
                : "text-muted-foreground",
            )}
          >
            {hint.text}
          </p>
        )}
      </div>

      {/* Estimates */}
      <div className="flex items-center justify-between pb-1.5 pt-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Estimates
          </span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
            {estimates.length + (linkedTotal > 0 ? 1 : 0)}
          </span>
        </div>
        {canEdit && !showAddForm && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 border-violet-200 text-xs text-violet-600 dark:border-violet-800 dark:text-violet-400"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-3 w-3" />
            Add estimate
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {linkedTotal > 0 && (
          <CalculatedCard
            total={linkedTotal}
            debts={debts}
            isSelected={!hasSelection}
            canEdit={canEdit}
            onSelect={() => {
              if (hasSelection) useCalculated.mutate({ entryId });
            }}
          />
        )}

        {orderedEstimates.map((estimate) => (
          <EstimateCard
            key={estimate.id}
            estimate={estimate}
            isSelected={estimate.id === selectedId}
            canEdit={canEdit}
            onSelect={() =>
              selectEstimate.mutate({ entryId, estimateId: estimate.id })
            }
            onUpdateAmount={(amount) =>
              updateEstimate.mutate({
                entryId,
                estimateId: estimate.id,
                amount,
              })
            }
            onDelete={() =>
              deleteEstimate.mutate({ entryId, estimateId: estimate.id })
            }
          />
        ))}

        {estimates.length === 0 && linkedTotal === 0 && !showAddForm && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No estimates yet.
          </p>
        )}

        {showAddForm && (
          <AddEstimateForm
            onAdd={(input) => {
              addEstimate.mutate({ entryId, ...input });
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    </>
  );
}
